import { useState } from 'react';
import { Check, Globe } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/atoms/Button';
import { DropdownMenu } from '@/components/ui/organisms/DropdownMenu';
import { useLocale } from '@/i18n/LocaleProvider';
import { LOCALE_FULL_NAMES, SUPPORTED_LOCALES, type Locale } from '@/i18n/config';

import { LanguageSwitchConfirmDialog } from './LanguageSwitchConfirmDialog';

interface Props {
  /** Visual variant.
   *  - `icon`: icon-only square button. The default — fits anywhere
   *    without competing with surrounding text or CTAs.
   *  - `subtle`: same icon button at app-topbar weight. */
  variant?: 'icon' | 'subtle';
  /** Whether the menu opens above or below the trigger. */
  dropDirection?: 'down' | 'up';
  /** Anchor side of the menu (which edge of the trigger it lines up with). */
  align?: 'left' | 'right';
  /** Optional controlled locale used for draft selection UIs. */
  selectedLocale?: Locale;
  /** Optional draft callback. If provided, no immediate locale switch happens. */
  onSelectLocale?: (next: Locale) => void;
}

// Icon-only locale switcher. Trigger uses the Button atom (`type="icon"`).
// Menu uses DropdownMenu (portal, Escape, arrows, focus restore).
//
// Auth handling:
//   • Anonymous  → switches locale immediately. No modal or backend call.
//   • Authenticated → opens the workspace confirmation modal first; locale
//     changes only after the user confirms (API + setLocale in the dialog).
export function LanguageGlobe({
  align = 'right',
  selectedLocale,
  onSelectLocale,
}: Props) {
  const { t } = useTranslation('common');
  const { locale, setLocale } = useLocale();
  const activeLocale = selectedLocale ?? locale;
  const { isAuthenticated } = useAuth0();
  const [workspaceConfirmLocale, setWorkspaceConfirmLocale] = useState<Locale | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const onPick = async (next: Locale) => {
    if (next === activeLocale) return;

    if (onSelectLocale) {
      onSelectLocale(next);
      return;
    }

    if (isAuthenticated) {
      setWorkspaceConfirmLocale(next);
      return;
    }

    await setLocale(next);
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenu.Trigger asChild>
          <Button
            type="icon"
            intent="muted"
            size="md"
            icon={<Globe />}
            selected={menuOpen}
            aria-label={`${t('language.switcher_aria_label')}, ${LOCALE_FULL_NAMES[activeLocale]}`}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align={align === 'left' ? 'start' : 'end'}>
          <DropdownMenu.Label className="uppercase">
            {t('language.menu_title')}
          </DropdownMenu.Label>
          {SUPPORTED_LOCALES.map((loc) => {
            const active = loc === activeLocale;
            return (
              <DropdownMenu.Item
                key={loc}
                onClick={() => void onPick(loc)}
                className={active ? 'justify-between font-semibold text-dark-invirtus' : 'justify-between'}
              >
                <span className="truncate">{LOCALE_FULL_NAMES[loc]}</span>
                {active ? <Check aria-hidden /> : null}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu>

      <LanguageSwitchConfirmDialog
        isOpen={workspaceConfirmLocale !== null}
        pendingLocale={workspaceConfirmLocale}
        onClose={() => setWorkspaceConfirmLocale(null)}
      />
    </>
  );
}
